import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, CalendarCheck, Wallet, FileSpreadsheet, Download, Heart } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const zar = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function ChildrenPage() {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/parent/children");
        setChildren(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const downloadPdf = (child) => {
    window.open(`${API_BASE}/exams/report/${child.id}/pdf`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="children-page">
      <div>
        <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Family</div>
        <h1 className="text-4xl font-bold tracking-tight flex items-center gap-3">
          <Heart className="h-8 w-8 text-primary" /> My Children
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Your parent-only view. You see only data belonging to your child(ren).</p>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : children.length === 0 ? (
        <Card className="rounded-2xl border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No children linked yet. Contact your school office — they will link your child to your parent email.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {children.map((c) => (
            <Card key={c.id} className="rounded-2xl lift" data-testid={`child-${c.id}`}>
              <CardHeader className="flex flex-row items-center gap-4">
                <Avatar className="h-14 w-14">
                  <AvatarFallback className="bg-primary/15 text-primary">{initials(c.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-xl">{c.name}</CardTitle>
                  <div className="mt-1 flex gap-2 items-center flex-wrap">
                    <Badge variant="outline" className="rounded-full text-[0.65rem]">
                      <GraduationCap className="h-3 w-3 mr-1" /> Grade {c.grade}
                    </Badge>
                    <Badge className="rounded-full bg-muted text-foreground hover:bg-muted text-[0.65rem]">
                      {c.class_name || "Unassigned"}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-muted/40">
                    <div className="flex items-center gap-2 text-[0.65rem] tracking-widest uppercase text-muted-foreground">
                      <CalendarCheck className="h-3 w-3" /> Attendance
                    </div>
                    <div className="mt-2 text-2xl font-bold">{c.attendance_pct}%</div>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/40">
                    <div className="flex items-center gap-2 text-[0.65rem] tracking-widest uppercase text-muted-foreground">
                      <Wallet className="h-3 w-3" /> Fees Due
                    </div>
                    <div className={`mt-2 text-2xl font-bold ${c.fees_due > 0 ? "text-red-500" : "text-emerald-500"}`}>
                      {zar(c.fees_due)}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => downloadPdf(c)} className="rounded-xl gap-1" data-testid={`child-pdf-${c.id}`}>
                    <Download className="h-3.5 w-3.5" /> Download Report PDF
                  </Button>
                  <Link to="/fees">
                    <Button size="sm" variant="outline" className="rounded-xl gap-1">
                      <Wallet className="h-3.5 w-3.5" /> View fees
                    </Button>
                  </Link>
                  <Link to="/exams">
                    <Button size="sm" variant="outline" className="rounded-xl gap-1">
                      <FileSpreadsheet className="h-3.5 w-3.5" /> Report card
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
